/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import useGetParent from "hooks/useGetParent";

const StudentContext = createContext(undefined);

const StudentProvider = ({ children }) => {
    const { parent, error, isLoading } = useGetParent();
    const [selectedStudentId, setSelectedStudentId] = useState(null);
    const queryClient = useQueryClient();

    const students = useMemo(() => parent?.observing ?? [], [parent]);

    const activeSelectedStudentId = useMemo(() => {
        if (students.length === 0) {
            return null;
        }

        const hasSelectedStudent = students.some(
            (student) => student.emailAddress === selectedStudentId,
        );

        return hasSelectedStudent
            ? selectedStudentId
            : students[0].emailAddress;
    }, [selectedStudentId, students]);

    useEffect(() => {
        if (!activeSelectedStudentId) {
            return;
        }

        queryClient.invalidateQueries({
            predicate: (query) => query.queryKey[0] !== "parent",
        });
    }, [activeSelectedStudentId, queryClient]);

    const selectedStudent = useMemo(
        () =>
            students.find(
                (student) => student.emailAddress === activeSelectedStudentId,
            ) ?? null,
        [activeSelectedStudentId, students],
    );

    const value = useMemo(
        () => ({
            students,
            selectedStudent,
            selectedStudentId: activeSelectedStudentId,
            setSelectedStudentId,
            parent,
            error,
            isLoading,
        }),
        [
            students,
            selectedStudent,
            activeSelectedStudentId,
            parent,
            error,
            isLoading,
        ],
    );

    return (
        <StudentContext.Provider value={value}>
            {children}
        </StudentContext.Provider>
    );
};

const useStudentContext = () => {
    const context = useContext(StudentContext);

    if (!context) {
        throw new Error(
            "useStudentContext must be used within a StudentProvider",
        );
    }

    return context;
};

export { StudentProvider, useStudentContext };
